import fs from "node:fs/promises";
import path from "node:path";
import { createInstallPlan } from "../../core/install-plan.js";
import { buildAiWorkflowConfig, getTemplateFiles, isValidProfile } from "../../core/templates.js";
import { exists, writeFileSafe, readJson } from "../../core/filesystem.js";
import { createManagedBackup, createManagedPathBackup } from "../../core/backup.js";
import { mergeOpencodeConfig } from "../../core/opencode-merge.js";
import { buildSymlinkEntries, isSymlinkTo, setupInternalSymlinks } from "../../core/symlink-layout.js";
import { ClaudeAdapter, CodexAdapter, AntigravityAdapter } from "../../adapters/index.js";

function summarize(actions: any[]) {
  const createCount = actions.filter((a) => a.type === "create").length;
  const conflictCount = actions.filter((a) => a.type === "conflict").length;

  return { createCount, conflictCount };
}

function printPlan(actions: any[]) {
  for (const action of actions) {
    console.log(`- ${action.type.padEnd(8)} ${action.relativePath}`);
  }
}

function toIgnoreEntries(linkPath: string): string[] {
  if (linkPath === "README.workflow.md") {
    return [linkPath];
  }

  const normalized = linkPath.endsWith("/") ? linkPath.slice(0, -1) : linkPath;
  return [normalized, `${normalized}/`];
}

function buildGitignoreBlock(entries: string[]): string {
  const unique = Array.from(new Set(entries));
  const sorted = unique.sort((a, b) => a.localeCompare(b));

  return [
    "# BEGIN AI WORKFLOW KIT",
    "# AI Workflow Kit generated files",
    ...sorted,
    "# END AI WORKFLOW KIT"
  ].join("\n");
}

async function upsertGitignoreBlock(cwd: string, entries: string[]): Promise<string> {
  const gitignorePath = path.join(cwd, ".gitignore");
  const block = buildGitignoreBlock(entries);
  const beginMarker = "# BEGIN AI WORKFLOW KIT";
  const endMarker = "# END AI WORKFLOW KIT";

  const hasGitignore = await exists(gitignorePath);
  if (!hasGitignore) {
    await writeFileSafe(gitignorePath, `${block}\n`);
    return "created";
  }

  const current = await fs.readFile(gitignorePath, "utf8");
  const beginIndex = current.indexOf(beginMarker);
  const endIndex = current.indexOf(endMarker);

  if (beginIndex >= 0 && endIndex > beginIndex) {
    const before = current
      .slice(0, beginIndex)
      .replace(/\n?# AI Workflow Kit generated files\n?$/, "\n")
      .replace(/[ \t]+$/gm, "")
      .replace(/\n*$/, "\n");
    const afterStart = endIndex + endMarker.length;
    const after = current.slice(afterStart).replace(/^\n*/, "\n");
    const next = `${before}${block}${after}`;
    if (next !== current) {
      await writeFileSafe(gitignorePath, next);
      return "updated";
    }
    return "unchanged";
  }

  const separator = current.endsWith("\n") ? "\n" : "\n\n";
  const next = `${current}${separator}${block}\n`;
  await writeFileSafe(gitignorePath, next);
  return "updated";
}

async function injectScripts(cwd: string): Promise<boolean> {
  const pkgPath = path.join(cwd, "package.json");
  if (!(await exists(pkgPath))) return false;

  try {
    const pkg = await readJson(pkgPath);
    if (!pkg.scripts) pkg.scripts = {};

    if (!pkg.scripts.validate) {
      pkg.scripts.validate = "ai-workflow collect-evidence";
      await writeFileSafe(pkgPath, `${JSON.stringify(pkg, null, 2)}\n`);
      return true;
    }
  } catch {
    // Ignore invalid package.json
  }
  return false;
}

export interface InitOptions {
  cwd: string;
  yes?: boolean;
  force?: boolean;
  dryRun?: boolean;
  noInstall?: boolean;
  noOverwrite?: boolean;
  claude?: boolean;
  codex?: boolean;
  antigravity?: boolean;
  "dev-mode"?: boolean;
  profile?: string;
}

async function verifySelfExecutionGuard(cwd: string, devMode?: boolean): Promise<void> {
  if (!devMode) {
    try {
      const pkgPath = path.join(cwd, "package.json");
      if (await exists(pkgPath)) {
        const pkg = await readJson(pkgPath);
        if (pkg.name === "@williambeto/ai-workflow") {
          console.error("\nFATAL: Cannot run 'ai-workflow init' inside its own development repository.");
          console.error("This would corrupt the development environment. Aborting.");
          console.error("Use the --dev-mode flag if you are developing the kit itself and need to test the init command.\n");
          process.exit(1);
        }
      }
    } catch {
      // Ignore if package.json is unreadable. The guard is a safety net.
    }
  }
}

function resolveProfile(profile?: string): string {
  const selectedProfile = profile || "standard";
  if (!isValidProfile(selectedProfile)) {
    console.error(`Invalid profile: "${selectedProfile}". Valid profiles: standard, full`);
    process.exit(1);
  }
  return selectedProfile;
}

async function writeInstallFiles(
  cwd: string,
  actions: any[],
  selectedProfile: string,
  platforms: string[],
  force?: boolean,
  noOverwrite?: boolean,
  backupRoot = ".ai-workflow-backups"
): Promise<{ skipped: string[]; backups: string[] }> {
  const skipped: string[] = [];
  const backups: string[] = [];

  for (const action of actions) {
    if (action.type === "conflict" && (noOverwrite || !force)) {
      skipped.push(action.relativePath);
      continue;
    }

    if (action.type === "conflict" && force) {
      const backupPath = await createManagedBackup(action.absolutePath, {
        cwd,
        backupRoot,
        maxPerFile: 20
      });
      backups.push(backupPath);
    }

    const content =
      action.relativePath === ".ai-workflow/config.json"
        ? `${JSON.stringify(buildAiWorkflowConfig({ profile: selectedProfile, runtimes: platforms.length > 0 ? platforms : ["opencode"] }), null, 2)}\n`
        : action.content;

    await writeFileSafe(action.absolutePath, content || "");
  }

  return { skipped, backups };
}

async function writeSymlinks(
  cwd: string,
  linkEntries: any[],
  force?: boolean,
  backupRoot = ".ai-workflow-backups"
): Promise<{ linkBackups: string[]; linkCreated: string[] }> {
  const linkBackups: string[] = [];
  const linkCreated: string[] = [];

  for (const { linkPath, targetPath } of linkEntries) {
    const absoluteLinkPath = path.join(cwd, linkPath);
    const absoluteTargetPath = path.join(cwd, targetPath);

    if (!(await exists(absoluteTargetPath))) {
      throw new Error(`missing link target: ${targetPath}`);
    }

    if (await isSymlinkTo(absoluteLinkPath, absoluteTargetPath)) {
      continue;
    }

    if (await exists(absoluteLinkPath)) {
      if (!force) {
        throw new Error(
          `cannot create symlink '${linkPath}' because path already exists. Re-run with --force to backup and replace. If symlink creation fails, see troubleshooting in docs/npm-consumer-quickstart.md.`
        );
      }
      const backupPath = await createManagedPathBackup(absoluteLinkPath, {
        cwd,
        backupRoot,
        maxPerFile: 20
      });
      linkBackups.push(backupPath);
      await fs.rm(absoluteLinkPath, { recursive: true, force: true });
    }

    const stat = await fs.lstat(absoluteTargetPath);
    const type = process.platform === "win32"
      ? (stat.isDirectory() ? "junction" : "file")
      : (stat.isDirectory() ? "dir" : "file");
    const linkTarget = process.platform === "win32"
      ? absoluteTargetPath
      : path.relative(path.dirname(absoluteLinkPath), absoluteTargetPath);

    try {
      await fs.mkdir(path.dirname(absoluteLinkPath), { recursive: true });
      await fs.symlink(linkTarget, absoluteLinkPath, type);
    } catch (error: any) {
      throw new Error(
        `failed to create symlink '${linkPath}' -> '${targetPath}' (${error.message}). ` +
          "For Windows, enable Developer Mode or run terminal as Administrator. " +
          "For Linux/macOS, verify write permissions in project root."
      );
    }
    linkCreated.push(linkPath);
  }

  return { linkBackups, linkCreated };
}

async function executePlatformMigrations(
  cwd: string,
  installRoot: string,
  platforms: string[],
  claude?: boolean,
  codex?: boolean,
  antigravity?: boolean,
  force?: boolean
): Promise<void> {
  if (platforms.length === 0) return;

  console.log("ai-workflow: migrating to native platforms...");
  const agentsDir = path.join(cwd, installRoot, "opencode/agents");
  const skillsDir = path.join(cwd, installRoot, "opencode/skills");

  const agentFiles = (await fs.readdir(agentsDir).catch(() => []))
    .filter((f) => f.endsWith(".md"))
    .map((f) => path.join(agentsDir, f));

  if (claude) {
    const adapter = new ClaudeAdapter({ cwd });
    await adapter.deploy(installRoot);
    console.log("- claude: deployed AI Workflow Kit agents to CLAUDE.md and .claude/rules/ (native discovery enabled)");
  }

  if (codex) {
    const adapter = new CodexAdapter({ cwd });
    await adapter.deploy(installRoot, force);
    console.log("- codex: deployed AI Workflow Kit agents to .agents/, .codex/, and .github/ (native discovery enabled)");
  }

  if (antigravity) {
    const adapter = new AntigravityAdapter({ cwd });
    const skillFolders = await fs.readdir(skillsDir).catch(() => []);
    const skillFiles: string[] = [];
    for (const folder of skillFolders) {
      const skillPath = path.join(skillsDir, folder, "SKILL.md");
      if (await exists(skillPath)) {
        skillFiles.push(skillPath);
      }
    }

    const allFiles = [...agentFiles, ...skillFiles];
    const transformed = await Promise.all(allFiles.map((f) => adapter.transform(f)));
    await adapter.deploy(transformed, installRoot);
    console.log(`- antigravity: deployed AI Workflow Kit agents to .agents/ (native discovery enabled)`);
  }
}

function printInstallationSummary({
  selectedProfile,
  opencodeResult,
  gitignoreResult,
  scriptInjected,
  linkCreated,
  backups,
  linkBackups,
  skipped,
  noInstall
}: {
  selectedProfile: string;
  opencodeResult: any;
  gitignoreResult: string;
  scriptInjected: boolean;
  linkCreated: string[];
  backups: string[];
  linkBackups: string[];
  skipped: string[];
  noInstall?: boolean;
}): void {
  console.log("Installation complete.");
  console.log(`- profile: ${selectedProfile}`);
  console.log(`- opencode.jsonc: ${opencodeResult.reason}`);
  console.log(`- .gitignore: ${gitignoreResult}`);
  console.log(`- scripts: ${scriptInjected ? "injected 'validate'" : "already-up-to-date"}`);
  console.log(`- symlink layout: ${linkCreated.length > 0 ? `created ${linkCreated.length}` : "already-up-to-date"}`);

  if (backups.length > 0) {
    console.log(`- backups created: ${backups.length}`);
    for (const filePath of backups) {
      console.log(`  ${filePath}`);
    }
  }
  if (linkBackups.length > 0) {
    console.log(`- symlink backups created: ${linkBackups.length}`);
    for (const filePath of linkBackups) {
      console.log(`  ${filePath}`);
    }
  }
  if (skipped.length > 0) {
    console.log(`- conflicts skipped: ${skipped.length}`);
    for (const relativePath of skipped) {
      console.log(`  ${relativePath}`);
    }
  }
  console.log(`- dependency install: ${noInstall ? "skipped (--no-install)" : "not managed in this step"}`);
  console.log("Next steps:");
  console.log("  npx @williambeto/ai-workflow doctor");
}

export async function runInit({
  cwd, yes, force, dryRun, noInstall, noOverwrite, claude, codex, antigravity, "dev-mode": devMode, profile
}: InitOptions): Promise<void> {
  await verifySelfExecutionGuard(cwd, devMode);

  const selectedProfile = resolveProfile(profile);
  const installRoot = ".ai-workflow";
  const backupRoot = ".ai-workflow-backups";

  const platforms: string[] = [];
  if (claude) platforms.push("claude");
  if (codex) platforms.push("codex");
  if (antigravity) platforms.push("antigravity");

  console.log(`ai-workflow: initializing in ${cwd}`);
  console.log(`Profile: ${selectedProfile}`);
  if (platforms.length > 0) {
    console.log(`Platforms: ${platforms.join(", ")}`);
  }

  const actions = await createInstallPlan({ cwd, exists, profile: selectedProfile });
  const linkEntries = buildSymlinkEntries({ templateFiles: getTemplateFiles(selectedProfile), installRoot });
  const { createCount, conflictCount } = summarize(actions);

  console.log(`Plan: ${createCount} create, ${conflictCount} conflict`);
  printPlan(actions);

  if (!yes && conflictCount > 0 && !force && !noOverwrite) {
    throw new Error(
      "conflicts detected. Re-run with --force to replace managed files or resolve conflicts manually"
    );
  }

  if (dryRun) {
    console.log("Dry-run enabled. No files were written.");
    if (platforms.length > 0) {
      console.log("ai-workflow: (dry-run) platform migration would be executed after file writing.");
    }
    return;
  }

  const { skipped, backups } = await writeInstallFiles(cwd, actions, selectedProfile, platforms, force, noOverwrite, backupRoot);
  const { linkBackups, linkCreated } = await writeSymlinks(cwd, linkEntries, force, backupRoot);

  await setupInternalSymlinks(cwd, installRoot);

  const opencodeResult = await mergeOpencodeConfig(cwd, { force, backupRoot });

  const generatedIgnoreEntries = [
    "node_modules/",
    ".ai-workflow/",
    ".ai-workflow-backups/",
    "opencode.jsonc.backup.*",
    "EVIDENCE.json",
    "*.tgz",
    ...linkEntries.flatMap((entry) => toIgnoreEntries(entry.linkPath))
  ];

  if (codex) {
    generatedIgnoreEntries.push(".agents/", ".codex/");
  }
  if (claude) {
    generatedIgnoreEntries.push(".claude/");
  }
  if (antigravity) {
    generatedIgnoreEntries.push(".agents/");
  }

  const gitignoreResult = await upsertGitignoreBlock(cwd, generatedIgnoreEntries);
  const scriptInjected = await injectScripts(cwd);

  await executePlatformMigrations(cwd, installRoot, platforms, claude, codex, antigravity, force);

  printInstallationSummary({
    selectedProfile,
    opencodeResult,
    gitignoreResult,
    scriptInjected,
    linkCreated,
    backups,
    linkBackups,
    skipped,
    noInstall
  });
}
